import { Observable, of, Subscription } from 'rxjs'
import { FeathersService } from '../../feathers.service'
import { first, map, shareReplay, switchMap, tap } from 'rxjs/operators'
import { inject, Injectable, OnDestroy } from '@angular/core'
import { FeathersModel } from '../../../models/feathersModel'
import { NewableFeathersModel } from '../../../shared/utils/typeUtils'

@Injectable({
  providedIn: 'root',
})
export abstract class CodeListService<T extends FeathersModel> implements OnDestroy {
  abstract recordClass: NewableFeathersModel<T>
  TAG = 'CodeListService'

  private subscription: Subscription
  protected entity$: Observable<T[]>
  protected feathers: FeathersService

  protected constructor() {
    this.feathers = inject(FeathersService)
  }

  protected initObservable() {
    this.entity$ = this.feathers.find(this.recordClass, {}).pipe(
      map((records) => {
        return this.getFallBackData(records)
      }),
      tap((records: T[]) => {
        if (!records || records.length === 0) console.error(`No records found for ${this.recordClass.serviceName}`)
      }),
      shareReplay({ bufferSize: 1, refCount: true }),
    )

    this.subscription = this.entity$.subscribe()
  }

  /**
   * Gets all given codelist entities from DB, implements caching.
   */
  public getAll(): Observable<T[]> {
    if (!this.entity$) this.initObservable()
    return this.entity$
  }

  /**
   * Retrieves a single codelist record based on the provided identifier. Implements caching.
   *
   * @param {number} id The unique identifier of the record to be retrieved.
   * @return {Observable<T>} An Observable that emits the record corresponding to the provided ID or retrieves it uncached if not found locally.
   */
  public getById(id: number): Observable<T> {
    return this.getAll().pipe(
      map((records) => {
        const filtered = records.filter((record) => record.id === id)
        if (filtered.length > 0) return filtered[0]
        return undefined
      }),
      switchMap((record) => {
        if (record) return of(record)
        return getUncachedResult(id, this.feathers, this.recordClass)
      }),
    )

    function getUncachedResult(
      id: number,
      feathers: FeathersService,
      recordClass: NewableFeathersModel<T>,
    ): Observable<T> {
      return feathers.find(recordClass, { id }).pipe(
        map((c) => c[0]),
        first(),
      )
    }
  }

  protected getFallBackData(records: T[]) {
    return records
  }

  ngOnDestroy(): void {
    this.subscription.unsubscribe()
  }
}
